import { X, MapPin, Calendar, ExternalLink, Clock, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  RACE_TYPE_LABELS, 
  getRaceTypeBadgeClass,
  formatDateFull 
} from '../lib/utils';

export const RaceDetailModal = ({ race, onClose }) => {
  if (!race) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="race-detail-modal"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        
        {/* Modal */}
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Image */}
          <div className="relative h-48 overflow-hidden">
            <img
              src={race.race_type === 'trail' 
                ? 'https://images.unsplash.com/photo-1700667877860-74fc8a2dae6f?w=800&q=80'
                : race.race_type === 'kids'
                ? 'https://images.unsplash.com/photo-1720799359333-974848b7dd8b?w=800&q=80'
                : 'https://images.unsplash.com/photo-1591788114109-c391b0cfdd2f?w=800&q=80'
              }
              alt={race.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              data-testid="close-modal-btn"
            >
              <X size={20} />
            </button>
            
            {/* Title */}
            <div className="absolute bottom-4 left-4 right-4">
              <Badge className={`${getRaceTypeBadgeClass(race.race_type)} mb-2`}>
                {RACE_TYPE_LABELS[race.race_type]}
              </Badge>
              <h2 
                className="text-2xl font-bold text-white uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {race.name}
              </h2>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {/* Meta info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <Calendar className="text-red-600" size={20} />
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold">Data</p>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {formatDateFull(race.date)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <MapPin className="text-red-600" size={20} />
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold">Local</p>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {race.location}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Distances */}
            {race.distances && race.distances.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 mb-2">
                  Distâncias
                </h3>
                <div className="flex flex-wrap gap-2">
                  {race.distances.map((dist, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm font-bold text-slate-700 dark:text-slate-300"
                    >
                      {dist}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Price */}
            {race.price && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-red-700 dark:text-red-400 uppercase">
                    Preço de inscrição
                  </span>
                  <span className="text-2xl font-black text-red-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {race.price}
                  </span>
                </div>
              </div>
            )}
            
            {/* Description */}
            {race.description && (
              <div className="mb-6">
                <h3 className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 mb-2">
                  Descrição
                </h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {race.description}
                </p>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-3">
              {race.website && (
                <Button
                  asChild
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  <a 
                    href={race.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    data-testid="race-website-btn"
                  >
                    <ExternalLink className="mr-2" size={16} />
                    Ver Prova
                  </a>
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Fechar
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RaceDetailModal;
